import usePendingState from '../../Hooks/usePendingState';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';

import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useNotification } from '../../Contexts/NotificationContext';
import Loading from '../Loading';
import FileDropzone from './FileDropzone';

const FormEditImage = () => {
    const { setNotification } = useNotification();
    const [loading, setLoading] = useState(true);
    const [groups, setGroups] = useState([]);
    const [imageData, setImageData] = useState({
        nome: '',
        img_preview: '',
        baixavel: false,
        compartilhavel: false,
        notificar: false,
        grupo: '',
        img_arq: '',
    });

    const [imageErrorData, setImageErrorData] = useState({
        nome: [],
        img_preview: [],
        baixavel: [],
        compartilhavel: [],
        notificar: [],
        grupo: [],
        img_arq: [],
    });

    const [formLoading, setFormLoading, formPendingRef] = usePendingState();

    const navigate = useNavigate();
    const { id } = useParams();
    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setImageData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSelectChange = (name, selectedOption) => {
        setImageData((prevData) => ({
            ...prevData,
            [name]: selectedOption.value,
        }));
    };

    const resizeImageProportionally = (file, maxSize = 500) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (event) => {
                const img = new Image();

                img.onload = () => {
                    const ratio = img.width / img.height;
                    let targetWidth = img.width;
                    let targetHeight = img.height;

                    if (targetWidth > targetHeight) {
                        if (targetWidth > maxSize) {
                            targetWidth = maxSize;
                            targetHeight = maxSize / ratio;
                        }
                    } else {
                        if (targetHeight > maxSize) {
                            targetHeight = maxSize;
                            targetWidth = maxSize * ratio;
                        }
                    }

                    const canvas = document.createElement('canvas');
                    canvas.width = targetWidth;
                    canvas.height = targetHeight;

                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

                    canvas.toBlob((blob) => {
                        resolve(blob);
                    }, file.type);
                };

                img.onerror = reject;
                img.src = event.target.result;
            };

            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formPendingRef.current) return;
        setFormLoading(true);

        try {
            if (imageData.img_arq) {
                const croppedImageBlob = await resizeImageProportionally(imageData.img_arq);

                setImageData((prevData) => ({
                    ...prevData,
                    img_preview: croppedImageBlob,
                }));
            }

            const formData = new FormData();

            Object.keys(imageData).forEach((key) => {
                if (key !== 'img_preview' && key !== 'img_arq') {
                    if (typeof imageData[key] === 'boolean') {
                        formData.append(key, imageData[key] ? 1 : 0);
                    } else {
                        formData.append(key, imageData[key]);
                    }
                }
            });

            if (imageData.img_preview) {
                formData.append('img_preview', imageData.img_preview);
            }
            if (imageData.img_arq) {
                formData.append('img_arq', imageData.img_arq);
            }

            const response = await fetch(`${baseUrl}/manager/materiais/imagens/atualizar/${id}`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: formData,
            });
            if (response.ok) {
                setNotification({
                    type: 'success',
                    message: 'Imagem atualizada com sucesso!',
                    show: true,
                });
                navigate(`/manager/produtos/editar/${imageData.produto_id}`);
            } else {
                if (response.status === 422) {
                    const errors = await response.json();

                    setImageErrorData(errors);
                } else {
                    throw new Error('Erro ao atualizar imagem.');
                }
            }
        } catch (error) {
            setNotification({
                type: 'error',
                message: error.message,
                show: true,
            });
        } finally {
            setFormLoading(false);
        }
    };

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const [imageResponse, groupsResponse] = await Promise.all([
                    fetch(`${baseUrl}/manager/materiais/imagens/${id}`, {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                    }),
                    fetch(`${baseUrl}/manager/grupos?tipo=imagem`, {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                    }),
                ]);

                if (imageResponse.ok) {
                    const imageData = await imageResponse.json();
                    setImageData(imageData.imagem || []);
                }

                if (groupsResponse.ok) {
                    const groupsData = await groupsResponse.json();
                    setGroups(
                        groupsData.grupos.map((grupo) => ({
                            value: grupo.id,
                            label: grupo.nome,
                        }))
                    );
                }
            } finally {
                setLoading(false);
            }
        };

        fetchContent();
    }, []);

    if (loading)
        return (
            <div className="mx-auto py-20 flex justify-center">
                <div className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
            </div>
        );

    return (
        <>
            <Link
                to={`/manager/produtos/imagens/${imageData.produto_id}`}
                className="bg-slate-100 px-4 py-1.5 rounded border border-slate-300 float-right ml-2 transition-all hover:bg-slate-200"
            >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Voltar
            </Link>

            <h4 className="mb-6 text-xl font-bold text-black">Atualizar Imagem</h4>

            <div className="flex flex-col">
                <form onSubmit={handleSubmit} className="max-w-5xl pb-10">
                    <div className="mb-6 2xl:mb-10">
                        <div className="mb-3 flex gap-5 flex-col lg:flex-row">
                            <div className="w-full lg:w-3/4">
                                <label className="block text-sm font-medium mb-1">Nome</label>
                                <input
                                    type="text"
                                    name="nome"
                                    value={imageData.nome}
                                    onClick={() => setImageErrorData((prevData) => ({ ...prevData, nome: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1.5 2xl:py-2 border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                />
                                {imageErrorData.nome && imageErrorData.nome[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {imageErrorData.nome[0]}{' '}
                                        <span
                                            onClick={() => setImageErrorData((prevData) => ({ ...prevData, nome: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="mb-3 flex gap-5 flex-col lg:flex-row">
                            <div className="w-full lg:w-1/3">
                                <label className="block text-sm font-medium mb-1">Grupo</label>
                                <Select
                                    name="grupo"
                                    options={groups}
                                    value={groups.find((option) => option.value === imageData.grupo) || null}
                                    onClick={() => imageErrorData((prevData) => ({ ...prevData, grupo: [] }))}
                                    onChange={(selectedOption) => handleSelectChange('grupo', selectedOption)}
                                    placeholder="Selecione uma opção..."
                                    classNamePrefix="admin-select"
                                    className="product-select"
                                />
                                {imageErrorData.grupo && imageErrorData.grupo[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {imageErrorData.grupo[0]}{' '}
                                        <span
                                            onClick={() => setImageErrorData((prevData) => ({ ...prevData, grupo: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>

                            <div className="w-1/12">
                                <label className="block text-sm font-medium">Baixável?</label>

                                <label className="cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="baixavel"
                                        checked={imageData.baixavel}
                                        onChange={handleChange}
                                        className="sr-only peer"
                                    />
                                    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-0 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                                </label>

                                {imageErrorData.baixavel && imageErrorData.baixavel[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {imageErrorData.baixavel[0]}{' '}
                                        <span
                                            onClick={() => setImageErrorData((prevData) => ({ ...prevData, baixavel: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>

                            <div className="w-1/6">
                                <label className="block text-sm font-medium">Compartilhável?</label>

                                <label className="cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="compartilhavel"
                                        checked={imageData.compartilhavel}
                                        onChange={handleChange}
                                        className="sr-only peer"
                                    />
                                    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-0 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                                </label>

                                {imageErrorData.compartilhavel && imageErrorData.compartilhavel[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {imageErrorData.compartilhavel[0]}{' '}
                                        <span
                                            onClick={() => setImageErrorData((prevData) => ({ ...prevData, compartilhavel: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>

                            <div className="w-1/6">
                                <label className="block text-sm font-medium">Notificar usuários?</label>

                                <label className="cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="notificar"
                                        checked={imageData.notificar}
                                        onChange={handleChange}
                                        className="sr-only peer"
                                    />
                                    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-0 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                                </label>

                                {imageErrorData.notificar && imageErrorData.notificar[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {imageErrorData.notificar[0]}{' '}
                                        <span
                                            onClick={() => setImageErrorData((prevData) => ({ ...prevData, notificar: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="mb-3 flex gap-5 flex-col lg:flex-row">
                            {/* <div className="w-5/12">
                                <label className="block text-sm font-medium">Imagem prévia</label>

                                <InputFileImage 
                                    name="img_preview"
                                    imagem={`/assets/img/admin/image-select.png`} 
                                    size={{ largura: '320', altura: '430' }} 
                                    allowCrop={true} 
                                    onImageCrop={handleImageCrop}
                                />
                                {imageErrorData.img_preview && imageErrorData.img_preview[0] && (<span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">{imageErrorData.img_preview[0]} <span onClick={() => setImageErrorData(prevData => ({...prevData, img_preview: []}))} className="rotate-45 -my-2 p-2 cursor-pointer">+</span></span>)}
                            </div> */}

                            <div className="w-1/3">
                                <label className="block text-sm font-medium">Imagem</label>

                                <FileDropzone
                                    type="image"
                                    name="img_arq"
                                    value={imageData.img_arq}
                                    onChange={(file) => {
                                        setImageData((prev) => ({ ...prev, img_arq: file }));
                                        setImageErrorData((prevData) => ({ ...prevData, img_arq: [] }));
                                    }}
                                    onDelete={() => setImageData((prev) => ({ ...prev, img_arq: null }))}
                                />

                                {imageErrorData.img_arq && imageErrorData.img_arq[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {imageErrorData.img_arq[0]}{' '}
                                        <span
                                            onClick={() => setImageErrorData((prevData) => ({ ...prevData, img_arq: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>

                            {imageData.preview && (
                                <div className="w-1/3">
                                    <label className="block text-sm font-medium">Imagem atual</label>
                                    <img alt='imagem preview' src={imageData.preview} className="border border-slate-300 rounded-md" />
                                </div>
                            )}
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={formLoading}
                        aria-busy={formLoading}
                        className="w-full lg:w-3/4 bg-secondary text-white py-2 px-4 rounded-md hover:bg-opacity-80 disabled:bg-opacity-80 disabled:cursor-not-allowed"
                    >
                        {!formLoading ? 'Salvar' : <Loading />}
                    </button>
                </form>
            </div>
        </>
    );
};

export default FormEditImage;
